/**
 * Desktop product card — square image on top, badges + title + description
 * middle, price + action footer. Used in the grid at sm+ breakpoints.
 *
 * Extracted from `app/(main)/products/ProductsClient.tsx` lines 363–499.
 *
 * Shares its buy-state derivation, title link, badge clusters and action
 * control with `ProductCardMobile.tsx` (see `product-card-model.ts`,
 * `ProductCardTitleLink.tsx`, `ProductCardBadges.tsx`,
 * `ProductCardActions.tsx`). What stays here is what genuinely differs: the
 * shadcn `Card`/`AspectRatio` shell, the hover-reveal share + zoom overlay, the
 * description preview, the `eager` LCP hint, and every class string. The two
 * cards' rendered markup is pinned byte-for-byte by
 * `tests/unit/harness/product-card-dom-baseline.spec.tsx`.
 */

"use client";

import { useState } from "react";
import { Expand } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ImageWithFallback } from "@/components/ui/image-with-fallback";
import { ShareProductButton } from "@/components/share/ShareProductButton";
import type { Product } from "@/stores/product-store";
import { StaticVideoThumbnail } from "../media/StaticVideoThumbnail";
import { ProductCardActions } from "./ProductCardActions";
import { ProductCardOverlayBadges, ProductCardTagRow } from "./ProductCardBadges";
import { ProductCardTitleLink } from "./ProductCardTitleLink";
import { deriveProductCardModel } from "./product-card-model";
import {
  categoryDisplayLabel,
  descriptionPreview,
  isVideoKey,
} from "../product-card-utils";

export function ProductCard({
  product,
  onClick,
  onAddToCart,
  onZoom,
  eager = false,
}: {
  product: Product;
  onClick: () => void;
  onAddToCart: (p: Product, e: React.MouseEvent) => void;
  onZoom: () => void;
  /** Load the image eagerly (no lazy defer). Set on the first screenful of
   *  cards so above-the-fold images aren't deferred by next/image's default
   *  lazy loading — improves LCP on the products grid. */
  eager?: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const model = deriveProductCardModel(product);
  const { priceLabel, isOutOfStock } = model;
  const descPreview = descriptionPreview(product.description);
  // Card is a click target via mouse onClick on a non-interactive element —
  // we deliberately don't add `role="button"` because the card already
  // contains other interactive controls (View full image, Share, Add to
  // cart). Marking the card itself as a button creates a `nested-interactive`
  // a11y violation: assistive tech can't reliably announce buttons inside a
  // button. Keyboard users reach the same "open details" action via the
  // title button below, which is announced inside its <h3> heading.
  return (
    <Card
      data-testid="product-card"
      className="group/card flex cursor-pointer flex-col overflow-hidden border-border/60 bg-card gap-0 pt-0 transition-all duration-200 hover:border-border hover:shadow-lg md:py-0"
      onClick={onClick}
    >
      <AspectRatio ratio={1} className="relative shrink-0 overflow-hidden bg-muted">
        {!loaded && <Skeleton className="absolute inset-0 rounded-none" />}
        {isVideoKey(product.imageKey) ? (
          <StaticVideoThumbnail
            src={product.imageUrlRaw || product.imageUrl}
            className={`absolute inset-0 h-full w-full object-cover object-center transition-all duration-500 group-hover/card:scale-105 ${
              loaded ? "opacity-100" : "opacity-0"
            }`}
            onLoadedMetadata={() => setLoaded(true)}
            aria-label={product.title}
          />
        ) : (
          <ImageWithFallback
            src={product.imageUrlRaw || product.imageUrl}
            alt={`${product.title} — ${categoryDisplayLabel(product.category)}`}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            loading={eager ? "eager" : "lazy"}
            onLoad={() => setLoaded(true)}
            className={`object-cover object-center transition-all duration-500 group-hover/card:scale-105 ${
              loaded ? "opacity-100" : "opacity-0"
            }`}
            fallbackClassName="absolute inset-0"
          />
        )}
        {/* Overlay badges — top-left */}
        <ProductCardOverlayBadges
          product={product}
          isOutOfStock={isOutOfStock}
          layout="card"
        />
        {/* Lightbox zoom — bottom-right, visible on hover */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onZoom();
          }}
          // Touch target ≥44×44 on phones (WCAG 2.5.8), shrunk to 32px on
          // sm+ where pointers do the targeting and the smaller decorative
          // button reads cleaner over the image. Pattern matches the
          // CLAUDE.md mobile-UX recipe: min-h/min-w floor + sm: opt-out.
          className="absolute bottom-2 right-2 flex min-h-11 min-w-11 size-11 items-center justify-center rounded-full bg-card/90 text-foreground shadow-sm opacity-0 backdrop-blur-sm transition-all duration-200 hover:bg-card hover:scale-110 group-hover/card:opacity-100 group-focus-within/card:opacity-100 focus-visible:opacity-100 sm:size-8 sm:min-h-0 sm:min-w-0"
          aria-label="View full image"
        >
          <Expand className="h-3.5 w-3.5" />
        </button>
        {/* Share — bottom-left, visible on hover */}
        <div
          className="absolute bottom-2 left-2 opacity-0 transition-opacity duration-200 group-hover/card:opacity-100 group-focus-within/card:opacity-100 focus-visible:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <ShareProductButton
            productId={product.id}
            productSlug={product.slug}
            productTitle={product.title}
            variant="secondary"
            size="icon"
            className="min-h-11 min-w-11 size-11 shrink-0 rounded-full bg-card/90 text-foreground shadow-sm backdrop-blur-sm hover:bg-card sm:size-8 sm:min-h-0 sm:min-w-0"
          />
        </div>
      </AspectRatio>
      <CardContent className="flex flex-1 flex-col gap-1.5 px-3 pt-3 pb-2">
        <ProductCardTagRow product={product} layout="card" />
        <h3 className="font-serif text-base font-semibold leading-snug text-foreground group-hover/card:text-primary transition-colors duration-200">
          <ProductCardTitleLink
            product={product}
            onOpen={onClick}
            // Single-line truncation: long titles collapse to "First few words…"
            // instead of wrapping to a second row. Keeps card heights aligned
            // across the grid and prevents the overlong-name card from looking
            // visually heavier than its neighbours. The native title tooltip
            // still surfaces the full text on hover. min-h-11 gives
            // mobile users a compliant touch target.
            // -mb only (was -my): the symmetric negative margin pulled the
            // title up into the tag row so they visually touched. The text is
            // top-aligned in the min-h box, so dropping the top pull restores a
            // clean gap below the tags; -mb keeps the title↔description spacing
            // compact and the card height in check.
            className="block w-full min-h-11 -mb-2.5 truncate text-left text-inherit hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:rounded-sm"
          />
        </h3>
        {descPreview ? (
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {descPreview}
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="flex flex-row items-center justify-between gap-2 border-t border-border/40 px-3 py-2.5">
        <p className="text-lg font-bold tabular-nums text-foreground">
          {priceLabel}
        </p>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <ProductCardActions
            product={product}
            model={model}
            onOpen={onClick}
            onAddToCart={onAddToCart}
            className="min-h-11 min-w-11 size-11 shrink-0 rounded-full bg-primary hover:bg-primary/90 sm:size-9 sm:min-h-0 sm:min-w-0"
          />
        </div>
      </CardFooter>
    </Card>
  );
}
